import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import DrawIcon from "@mui/icons-material/DrawOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActionsOutlined";
import {
  Timestamp,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useSession } from "../auth/AuthProvider";
import { formatJalaliInstant, formatNumber } from "../lib/format";
import { ContractDialog } from "../components/ContractDialog";
import { PageHeader, StatCard } from "../components/ui";
import { useT } from "../i18n";
import type { StringKey } from "../i18n/fa";

interface ContractRow {
  id: string;
  bookingId: string;
  status: string;
  sha256: string;
  templateVersion: number;
  createdAt: Timestamp | null;
  signedAt: Timestamp | null;
}

const TABS: { value: string; labelKey: StringKey }[] = [
  { value: "all", labelKey: "contracts_all" },
  { value: "issued", labelKey: "contracts_awaiting_signature" },
  { value: "signed", labelKey: "contracts_signed" },
];

const MAX_ROWS = 100;

export function ContractsPage() {
  const { orgId } = useSession();
  const t = useT();
  const [rows, setRows] = useState<ContractRow[]>([]);
  const [tab, setTab] = useState<string>("all");
  const [view, setView] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    // Contracts are a top-level collection readable by org members (firestore.rules
    // §contracts), so the list is realtime — a customer signing on the phone flips
    // the row here without a refresh.
    const q = query(
      collection(db, "contracts"),
      where("orgId", "==", orgId),
      orderBy("createdAt", "desc"),
      limit(MAX_ROWS),
    );
    return onSnapshot(
      q,
      (snap) => {
        setError(null);
        // Pick fields explicitly: every contract doc carries the full sealed `html`
        // (tens of KB) and it is not needed for a list — ContractDialog fetches the
        // document over REST when a row is opened.
        setRows(
          snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              bookingId: (data.bookingId as string) ?? "—",
              status: (data.status as string) ?? "issued",
              sha256: (data.sha256 as string) ?? "",
              templateVersion: (data.templateVersion as number) ?? 1,
              createdAt: (data.createdAt as Timestamp | null) ?? null,
              signedAt: (data.signedAt as Timestamp | null) ?? null,
            };
          }),
        );
      },
      (e) => setError(e.message),
    );
  }, [orgId]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      issued: rows.filter((r) => r.status === "issued").length,
      signed: rows.filter((r) => r.status === "signed").length,
    }),
    [rows],
  );

  const visible = tab === "all" ? rows : rows.filter((r) => r.status === tab);

  return (
    <>
      <PageHeader title={t("contracts_title")} subtitle={t("contracts_subtitle")} />

      <Grid container spacing={2} mb={1}>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<DescriptionIcon />}
            label={t("contracts_stat_total")}
            value={counts.all}
            hint={counts.all === MAX_ROWS ? t("contracts_stat_last_n", { n: MAX_ROWS }) : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<PendingActionsIcon />}
            label={t("contracts_awaiting_signature")}
            value={counts.issued}
            tone="warning"
            hint={counts.issued > 0 ? t("contracts_hint_unsigned") : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard icon={<DrawIcon />} label={t("contracts_signed")} value={counts.signed} tone="success" />
        </Grid>
      </Grid>

      {error && (
        <Typography color="error" variant="body2" mb={1}>
          {error}
        </Typography>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v as string)} sx={{ mb: 2 }}>
        {TABS.map((item) => (
          <Tab
            key={item.value}
            value={item.value}
            label={`${t(item.labelKey)} (${formatNumber(counts[item.value as keyof typeof counts])})`}
          />
        ))}
      </Tabs>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("contracts_col_issued")}</TableCell>
              <TableCell>{t("common_status")}</TableCell>
              <TableCell>{t("contracts_col_signed_date")}</TableCell>
              <TableCell>{t("contracts_col_template")}</TableCell>
              <TableCell>{t("contracts_col_seal")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {row.createdAt ? formatJalaliInstant(row.createdAt.toDate()) : "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status === "signed" ? t("contracts_status_signed") : t("contracts_awaiting_signature")}
                    color={row.status === "signed" ? "success" : "warning"}
                  />
                </TableCell>
                <TableCell>
                  {row.signedAt ? formatJalaliInstant(row.signedAt.toDate()) : "—"}
                </TableCell>
                <TableCell>{t("contracts_template_version", { v: row.templateVersion })}</TableCell>
                <TableCell>
                  <Tooltip title={row.sha256 || "—"}>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace", direction: "ltr", display: "inline-block" }}
                    >
                      {row.sha256 ? `${row.sha256.slice(0, 12)}…` : "—"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => setView(row.id)}>
                      {t("contracts_view_print")}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" textAlign="center" py={3}>
                    {rows.length === 0 ? t("contracts_empty_none") : t("contracts_empty_filtered")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <ContractDialog contractId={view} onClose={() => setView(null)} />
    </>
  );
}
